import React from 'react';
import { Routes, Route, useSearchParams } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CircularProgress } from '@mui/material';
import { ToastContainer } from 'react-toastify';
import theme from './utils/theme';
import './App.css';
import MainLayout from './components/layout/MainLayout';
import Login from './components/auth/Login';
import HubCallback from './pages/HubCallback';
import DocumentList from './components/documents/DocumentList';
import StudyFormTabs from './components/forms/StudyFormTabs';
import VersionHistory from './components/versions/VersionHistory';
import ClinicalIntakeForm from './components/forms/ClinicalIntakeForm';
import ClinicalIntakeHistory from './components/ClinicalIntakeHistory';
import ICFSectionHistory from './icf/components/ICFSectionHistory';
import SAPSectionHistory from './sap/components/SAPSectionHistory';
import INDSectionHistory from './ind/components/INDSectionHistory';
import INDPage from './ind/components/INDPage';
import DocumentEditor from './protocol/components/DocumentEditor';
import ICFEditor from './icf/components/ICFEditor';
import SAPEditor from './sap/components/SAPEditor';
import ProjectList from './components/projects/ProjectList';
import ProjectDetail from './components/projects/ProjectDetail';
import Dashboard from './components/dashboard/Dashboard';
import OnlyOfficeWorkflow from './components/documentEditor/OnlyOfficeWorkflow';
import WorkflowDashboard from './components/documentEditor/WorkflowDashboard';
import WorkflowList from './components/documentEditor/WorkflowList';
import { ProjectProvider } from './context/ProjectContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import 'react-toastify/dist/ReactToastify.css';
import ProfilePage from './components/ProfilePage';
import AmendmentPage from './components/AmendmentPage';
import SAPPage from './sap/components/SAPPage';
import Home from './components/Home';
import HelpCenter from './components/HelpCenter';
import DesignStudioPage from './components/designStudio/DesignStudioPage';
import TemplatesTab from './components/templates/TemplatesTab';
import ShowcasePage from './pages/ShowcasePage';
import WorkflowReviewGuidancePage from './components/workflow/WorkflowReviewGuidancePage';
import AuditTrail from './pages/AuditTrail';
import SignatureRedirect from './pages/SignatureRedirect';
import { IND_ENABLED } from './config/env';

const authStatusScreenStyle = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  width: '100%',
  gap: 16,
  fontSize: 16,
  color: '#555',
  textAlign: 'center',
  margin: 0,
  boxSizing: 'border-box',
};

const AuthStatusScreen = ({ message }) => (
  <div style={authStatusScreenStyle}>
    <CircularProgress />
    <div>{message}</div>
  </div>
);

const DesignStudioPageWrapper = () => {
  const [searchParams] = useSearchParams();
  return <DesignStudioPage projectId={searchParams.get('project')} />;
};

const TemplatesTabWrapper = () => {
  const [searchParams] = useSearchParams();
  return <TemplatesTab projectId={searchParams.get('project')} />;
};

// Protected Route — unauthenticated users go straight to Hub SSO (no local login UI)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, redirectToHubLogin } = useAuth();

  React.useEffect(() => {
    if (!loading && !isAuthenticated) {
      redirectToHubLogin();
    }
  }, [loading, isAuthenticated, redirectToHubLogin]);

  if (loading) {
    return <AuthStatusScreen message="Checking your session…" />;
  }

  if (!isAuthenticated) {
    return <AuthStatusScreen message="Redirecting to Hub sign-in…" />;
  }

  return children;
};

const AppContent = () => {
  const { loading } = useAuth();
  const [searchParams] = useSearchParams();
  const locationPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isAuthRoute = locationPath === '/login' || locationPath === '/callback' || locationPath === '/signature-redirect';
  const returningFromHub = locationPath === '/login' && Boolean(searchParams.get('token'));

  // Never block /login or /callback behind a global "Checking your session…"
  // screen — that flashed wrongly after Hub already signed the user in.
  if (loading && !isAuthRoute) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthStatusScreen message="Checking your session…" />
      </ThemeProvider>
    );
  }

  // Hub returned ?token= while AuthProvider validates it — show sign-in, not redirect.
  if (loading && returningFromHub) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthStatusScreen message="Signing you in…" />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer />
      <ProjectProvider>
        <Routes>
          {/* Public Routes - No Layout */}
          <Route path="/login" element={<Login />} />
          <Route path="/callback" element={<HubCallback />} />
          <Route path="/signature-redirect" element={<SignatureRedirect />} />

          {/* Protected Routes - With Layout */}
          <Route path="/*" element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/projects" element={<ProjectList />} />
                  <Route path="/projects/:projectId" element={<ProjectDetail />} />
                  <Route path="/documents" element={<DocumentList />} />
                  <Route path="/clinical-intake" element={<StudyFormTabs />} />
                  <Route path="/section-history/:sectionId" element={<VersionHistory />} />
                  <Route path="/icf-section-history/:id" element={<ICFSectionHistory />} />
                  <Route path="/sap-section-history/:id" element={<SAPSectionHistory />} />
                  {IND_ENABLED && <Route path="/ind-section-history/:id" element={<INDSectionHistory />} />}
                  <Route path="/new-form" element={<ClinicalIntakeForm />} />
                  <Route path="/new-form/:id" element={<ClinicalIntakeForm />} />
                  <Route path="/view-form/:id" element={<ClinicalIntakeForm isViewMode={true} />} />
                  <Route path="/history" element={<ClinicalIntakeHistory />} />
                  
                  {/* Direct Document Editor Routes */}
                  <Route path="/document-editor/study-form" element={<StudyFormTabs />} />
                  <Route path="/document-editor/protocol/:id" element={<DocumentEditor />} />
                  <Route path="/document-editor/icf/:id" element={<ICFEditor />} />
                  <Route path="/document-editor/sap/:id" element={<SAPEditor />} />

                  {/* Workflow Routes */}
                  <Route path="/documents/workflows" element={<WorkflowList />} />
                  <Route path="/documents/workflow/new" element={<OnlyOfficeWorkflow documentId="new" />} />
                  <Route path="/documents/workflow/:workflowId" element={<OnlyOfficeWorkflow />} />
                  <Route path="/documents/workflow-dashboard/:documentId" element={<WorkflowDashboard />} />

                  {/* Profile Route */}
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/help" element={<HelpCenter />} />
                  <Route path="/guidance/protocol-review" element={<WorkflowReviewGuidancePage />} />
                  {/* About Us should display the full Showcase UI */}
                  <Route path="/about" element={<ShowcasePage />} />
                  <Route path="/sap" element={<SAPPage />} />
                  {IND_ENABLED && <Route path="/ind" element={<INDPage />} />}
                  <Route path="/amendment" element={<AmendmentPage />} />
                  <Route path="/audit-trail" element={<AuditTrail />} />

                  {/* Design Studio & Templates standalone routes */}
                  <Route path="/design-studio" element={<DesignStudioPageWrapper />} />
                  <Route path="/templates" element={<TemplatesTabWrapper />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </ProjectProvider>
    </ThemeProvider>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

export default App; 